import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {UnmaskingService} from '@shared/api/services/v2/unmasking.service';
import {UnmaskingStateService} from '@shared/api/services/v2/unmasking-state.service';
import {AuthService} from '@shared/services/security/auth.service';
import {SensitiveDataMaskingConfig} from '@shared/config/sensitive-data-masking.config';
import {Roles} from '@models/roles.enum';
import {Subscription} from "rxjs";

@Component({
    selector: 'app-masked-field',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="masked-field-container d-inline-flex align-items-center"
             [ngClass]="{'noselect': maskingEnabled}">
            <span [ngClass]="{'unmasked-text': maskingEnabled && isUnmasked, 'user-select-none': maskingEnabled && !isUnmasked}"
                [style.color]="maskingEnabled && isUnmasked ? '#0d6efd' : 'inherit'">
            {{ isUnmasked ? fullValue : value }}
            </span>

            <button *ngIf="canReveal && !isUnmasked && value && !hideButton"
                    (click)="reveal()"
                    class="btn btn-sm btn-link p-0 ms-1 reveal-btn"
                    title="Reveal">
                <i class="bi bi-eye"></i> Reveal
            </button>

            <button *ngIf="canReveal && isUnmasked && !hideButton"
                    (click)="mask()"
                    class="btn btn-sm btn-link p-0 ms-1 reveal-btn"
                    title="Mask">
                <i class="bi bi-eye-slash"></i> Mask
            </button>
        </div>
    `,
    styles: [`
        .user-select-none {
            user-select: none;
        }

        .reveal-btn {
            font-size: 0.8rem;
            text-decoration: none;
            vertical-align: baseline;
        }

        .reveal-btn:hover {
            text-decoration: underline;
        }

        .unmasked-text {
            font-weight: 500;
        }

        .noselect {
            -moz-user-select: none;
            -webkit-user-select: none;
            -webkit-touch-callout: none;
            -ms-user-select: none;
            -o-user-select: none;
            user-select: none;
            -khtml-user-select: none;
        }
    `]
})
export class MaskedFieldComponent implements OnInit, OnDestroy {
    @Input() value: string;
    @Input() targetUserId: number;
    @Input() fieldName: string; // e.g., 'email', 'phoneNumber', 'nhsNumber', 'name'
    @Input() hideButton: boolean = false;
    readonly TIME_HIDE_FIELD: number = 30000;
    private timerHandle: any;
    private subscription: Subscription;

    isUnmasked: boolean = false;
    fullValue: string = '';
    canReveal: boolean = false;
    maskingEnabled: boolean = false;

    constructor(
        private unmaskingService: UnmaskingService,
        private unmaskingStateService: UnmaskingStateService,
        private authService: AuthService,
        private maskingConfig: SensitiveDataMaskingConfig
    ) {
    }

    ngOnInit() {
        // UNIVAPLAT-2788: masking temporarily deactivated platform-wide (Epic UNIVAPLAT-2776).
        // When the flag is off the component is dormant: the backend-supplied clear value is
        // rendered as plain, selectable text, no Reveal/Mask buttons, no auto-hide timer, and
        // no UnmaskingStateService subscription (services stay injected but idle).
        this.maskingEnabled = this.maskingConfig.enabled;
        if (!this.maskingEnabled) {
            this.canReveal = false;
            return;
        }

        const userRoles = this.authService.roles;
        // The "Reveal" functionality is available to users with the clinician and Study Manager (admin) roles.
        // UNIVAPLAT-1911: widened to include Roles.Admin for WhatsApp messaging log view.
        // Roles.Admin = 'Admin' — verified against roles.enum.ts. TODO: confirm JWT claim once UNIVAPLAT-1905 merges.
        this.canReveal = userRoles.includes(Roles.StudyManager) || userRoles.includes(Roles.Admin);

        this.subscription = this.unmaskingStateService.getUnmaskedState().subscribe(state => {
            const userState = state.find(item => item.userId === this.targetUserId);
            if (userState) {
                if (this.fieldName === 'name') {
                    if (userState.data.firstName || userState.data.lastName) {
                        this.fullValue = `${userState.data.firstName ?? ''} ${userState.data.lastName ?? ''}`;
                        this.isUnmasked = true;
                    }
                } else if (userState.data[this.fieldName]) {
                    this.fullValue = userState.data[this.fieldName] as string;
                    this.isUnmasked = true;
                }
            } else {
                this.isUnmasked = false;
                this.fullValue = '';
            }
        });
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    async reveal() {
        // UNIVAPLAT-2788: defensive guard — with masking deactivated the Reveal button never
        // renders, but ensure unmask() can never be called and the timer never starts.
        if (!this.maskingEnabled) return;
        if (!this.targetUserId || !this.fieldName) return;

        try {
            const fields = this.fieldName === 'name' ? ['firstName', 'lastName'] : [this.fieldName];
            const data = await this.unmaskingService.unmask({
                targetUserId: this.targetUserId,
                fields: fields
            });
            if (this.fieldName === 'name') {
                this.fullValue = `${data.firstName ?? ''} ${data.lastName ?? ''}`;
            } else {
                this.fullValue = data[this.fieldName];
            }
            this.isUnmasked = true;
            this.timerHandle = setTimeout(() => {
                this.mask();
            }, this.TIME_HIDE_FIELD);
        } catch (error) {
            console.error('Access Denied or API Error', error);
        }
    }

    mask() {
        if (this.timerHandle) {
            clearTimeout(this.timerHandle);
            this.timerHandle = null;
        }
        this.isUnmasked = false;
        this.fullValue = '';
    }
}
